import { useCallback, useSyncExternalStore } from "react";
import { weddingSubscription } from "@/lib/firestore/subscriptions/wedding";
import { NO_UID_KEY } from "@/lib/firestore/subscription-keys";
import {
  WEDDING_SNAPSHOT_SERVER,
  WEDDING_SNAPSHOT_SIGNED_OUT,
} from "@/lib/firestore/subscription-snapshots";
import { useEffectiveUid } from "./useAuth";
import type { Wedding } from "@amazing-wedding/shared-types";

export interface UseWeddingResult {
  wedding: Wedding | null;
  /** True until both owner/partner queries have fired at least once. */
  loading: boolean;
}

/**
 * Returns the user's primary wedding (or null when none exists).
 *
 * A user reaches a wedding either as its owner or as the invited partner, so
 * we subscribe to both queries and prefer the owned one. Free tier: max 1
 * wedding per user; the full multi-wedding picker arrives later.
 *
 * Backed by a refcounted shared Firestore subscription — one listener pair
 * per signed-in uid regardless of how many components call this hook.
 */
export function useWedding(): UseWeddingResult {
  const uid = useEffectiveUid();
  const key = uid ?? NO_UID_KEY;

  const subscribe = useCallback(
    (listener: () => void) => {
      if (!uid) return () => {};
      return weddingSubscription.subscribe(key, listener);
    },
    [uid, key],
  );

  const getSnapshot = useCallback(() => {
    if (!uid) return WEDDING_SNAPSHOT_SIGNED_OUT;
    return weddingSubscription.getSnapshot(key);
  }, [uid, key]);

  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => WEDDING_SNAPSHOT_SERVER);

  if (!uid) {
    return { wedding: null, loading: false };
  }

  return { wedding: snapshot.data, loading: snapshot.loading };
}
